import { HamburgerMenuIcon, MoonIcon, SunIcon } from '@radix-ui/react-icons'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePageStateStore } from '../../reducers'
import { useTranslation } from 'react-i18next'

type DarkModeMenuProps = {
  showSignOutButton?: boolean
  onSignOut?: () => void
}

export const DarkModeMenu = ({
  showSignOutButton = false,
  onSignOut,
}: DarkModeMenuProps) => {
  const getInitialMode = () => {
    const savedMode = localStorage.getItem('darkMode')
    if (savedMode !== null) {
      return savedMode === 'true'
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  }

  const getInitialLang = () => {
    const savedLang = localStorage.getItem('lang')
    if (savedLang !== null) {
      return savedLang
    }
    return 'en'
  }

  const { i18n, t } = useTranslation()
  const [darkMode, setDarkMode] = useState(getInitialMode)
  const [lang, setLang] = useState(getInitialLang)
  const navigate = useNavigate()
  const clearAuthToken = usePageStateStore.use.clearAuthToken()
  const toggleDarkMode = () => {
    const newMode = !darkMode
    setDarkMode(newMode)
    localStorage.setItem('darkMode', String(newMode))
    document.documentElement.classList.toggle('dark', newMode)
    window.parent.postMessage({ darkMode: newMode }, '*')
  }

  const toggleLangSupport = () => {
    const newLang = lang === 'en' ? 'fr' : 'en'
    setLang(newLang)
    localStorage.setItem('lang', newLang)
    i18n.changeLanguage(newLang)
  }

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [])

  useEffect(() => {
    const syncDarkModeAcrossTabs = (event: StorageEvent) => {
      if (event.key === 'darkMode') {
        setDarkMode(event.newValue === 'true')
      }
    }

    const handleParentMessage = (event: MessageEvent) => {
      if (event.data && typeof event.data.darkMode === 'boolean') {
        const newMode = event.data.darkMode
        setDarkMode(newMode)
        localStorage.setItem('darkMode', String(newMode))
        document.documentElement.classList.toggle('dark', newMode)
      }
    }

    window.addEventListener('storage', syncDarkModeAcrossTabs)
    window.addEventListener('message', handleParentMessage)

    return () => {
      window.removeEventListener('storage', syncDarkModeAcrossTabs)
      window.removeEventListener('message', handleParentMessage)
    }
  }, [])

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          className="IconButton"
          aria-label="menu"
          data-testid="btn-darkmode-menu"
        >
          <HamburgerMenuIcon className="w-8 h-8" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="bg-white dark:bg-pebble min-w-[120px] p-3"
          sideOffset={5}
          data-testid="container-darkmode-dropdown"
        >
          {showSignOutButton && (
            <DropdownMenu.Item
              className="flex items-center space-x-2 mb-2 cursor-pointer dark:text-snow text-pebble"
              onClick={() => {
                if (onSignOut) {
                  onSignOut()
                  return
                }
                clearAuthToken()
                navigate('/')
              }}
              data-testid="btn-darkmode-signout"
            >
              {t('widget.auth.signOut')}
            </DropdownMenu.Item>
          )}
          <DropdownMenu.CheckboxItem
            className="flex items-center space-x-2 cursor-pointer"
            checked={lang === 'en'}
            onCheckedChange={toggleLangSupport}
            data-testid="check-lang-toggle"
          >
            {lang === 'en' ? <p>FR</p> : <p>EN</p>}
          </DropdownMenu.CheckboxItem>
          <DropdownMenu.CheckboxItem
            className="flex items-center space-x-2 cursor-pointer"
            checked={darkMode}
            onCheckedChange={toggleDarkMode}
            data-testid="check-darkmode-toggle"
          >
            {darkMode ? (
              <SunIcon
                className="w-5 h-5"
                data-testid="icon-darkmode-menu-sun"
              />
            ) : (
              <MoonIcon
                className="w-5 h-5"
                data-testid="icon-darkmode-menu-moon"
              />
            )}
          </DropdownMenu.CheckboxItem>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
